import _ from 'lodash';
import { useObserver } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useQuery } from '@apollo/client';

import useUser from 'src/hooks/use-user';
import useCart from 'src/hooks/use-cart';
import { PersonalizedProductsQuery } from 'src/personalization/data-layer/types';
import { GetPersonalizedProductsDocument, PersonalizedProductsModelNames } from 'src/gql/graphql';
import { usePersonalizationEnabled } from 'src/utils/personalization/hooks/use-personalization-enabled';
import { usePersonalizedItemsV2 } from 'src/recommendations/use-personalized-items-v2';

type UseProductsForHomePageParams = {
  dispensaryId: string;
  skipApiCall?: boolean;
};

export type UseProductsForHomePageReturn = {
  personalizedProductsQuery: PersonalizedProductsQuery;
};

function useProductsForHomePageV2({
  dispensaryId,
  skipApiCall = false,
}: UseProductsForHomePageParams): UseProductsForHomePageReturn {
  const user = useUser();
  const isLoggedIn = useObserver(() => user.isLoggedIn);
  const isPersonalizationEnabledForHomePage = usePersonalizationEnabled();

  const personalizedProductLimit = isLoggedIn ? 20 : 3;

  const { data, loading, error } = usePersonalizedItemsV2(
    () => Promise.resolve([]),
    [], // No product context
    PersonalizedProductsModelNames.recommendedForYou,
    {
      limit: personalizedProductLimit,
      skip: skipApiCall || !dispensaryId || !isPersonalizationEnabledForHomePage,
    }
  );

  // Transform V2 data to match V1 interface for backward compatibility
  const transformedData = data
    ? {
        getPersonalizedProducts: {
          products: data,
        },
      }
    : undefined;

  return {
    personalizedProductsQuery: {
      data: transformedData,
      loading,
      error: error as any, // V2 returns Error, V1 expects ApolloError - cast for compatibility
    },
  };
}

function useProductsForHomePageV1({
  dispensaryId,
  skipApiCall = false,
}: UseProductsForHomePageParams): UseProductsForHomePageReturn {
  const user = useUser();
  const cart = useCart();
  const menuType = useObserver(() => cart.menuType);
  const isLoggedIn = useObserver(() => user.isLoggedIn);
  const isPersonalizationEnabledForHomePage = usePersonalizationEnabled();

  const personalizedProductLimit = isLoggedIn ? 20 : 3;

  const personalizedProductsQuery = useQuery(GetPersonalizedProductsDocument, {
    fetchPolicy: `network-only`,
    skip: skipApiCall || !dispensaryId || !isPersonalizationEnabledForHomePage,
    variables: {
      input: {
        dispensaryId,
        limit: personalizedProductLimit,
        filter: {
          menuType,
        },
      },
    },
  });

  return {
    personalizedProductsQuery: _.pick(personalizedProductsQuery, ['data', 'loading', 'error']),
  };
}
export function useProductsForHomePage({ dispensaryId }: UseProductsForHomePageParams): UseProductsForHomePageReturn {
  const flags = useFlags();
  const isPersonalizedProductsV2Enabled = flags['growth.platform.personalization-recommended-v2.rollout'] ?? false;

  const v2Result = useProductsForHomePageV2({ dispensaryId, skipApiCall: !isPersonalizedProductsV2Enabled });
  const v1Result = useProductsForHomePageV1({ dispensaryId, skipApiCall: isPersonalizedProductsV2Enabled });

  return isPersonalizedProductsV2Enabled ? v2Result : v1Result;
}
